<template>
    <div class="moment-setting wrapper" v-loading="loading">
        <proTitle></proTitle>

        <div v-show="view === 'list'" class="list-view-root" ref="listViewRoot">
            <div class="page-head flex-align-between">
                <span></span>
                <el-button type="primary" size="small" icon="el-icon-plus" @click="createSetting">新建精彩瞬间设置</el-button>
            </div>
            <div class="setting-list" ref="settingList" v-if="list.length">
                <div class="setting-item" v-for="item in list" :key="item.id">
                    <div class="setting-item-left">
                        <div class="setting-item-title">
                            {{ item.name || '未命名' }}
                            <el-tag size="mini" :type="item.status === 1 ? 'success' : 'info'">{{ item.status === 1 ?
                                '已启用' : '未启用' }}</el-tag>
                        </div>
                        <div class="setting-item-desc">
                            作答 {{ getItemDayCount(item) }} 天
                            · {{ mediaLabel(item) }}
                            · {{ formatGroupText(item) }}
                        </div>
                    </div>
                    <div class="setting-item-right">
                        <div class="setting-item-actions">
                            <el-button size="mini" plain :loading="item._toggling" @click="toggleStatus(item)">
                                {{ item.status === 1 ? '取消启用' : '启用' }}
                            </el-button>
                            <el-button size="mini" plain @click="openDetail(item.id, true)">查看</el-button>
                            <el-button size="mini" plain @click="openDetail(item.id, false)">编辑</el-button>
                            <el-button size="mini" plain @click="removeSetting(item)">删除</el-button>
                        </div>
                        <div class="setting-item-time">更新于 {{ formatUpdateTime(item.updateAt) }}</div>
                    </div>
                </div>
            </div>
            <el-empty v-else-if="!loading" description="暂无精彩瞬间设置，点击右上角「新建精彩瞬间设置」"></el-empty>
        </div>

        <div v-show="view === 'detail'">
            <div class="detail-back-bar">
                <div class="detail-back-left">
                    <el-button size="mini" icon="el-icon-arrow-left" @click="onBackToList">返回列表</el-button>
                    <span class="detail-label" v-if="detailLabel">{{ detailLabel }}</span>
                </div>
                <div>
                    <el-button v-if="readonly && editingId" size="mini" @click="readonly = false">编辑</el-button>
                    <el-button v-if="!readonly" type="primary" size="mini" :loading="saving"
                        @click="saveDetail(true)">保存</el-button>
                </div>
            </div>

            <div class="log-detail-shell">
                <div class="card-block">
                    <div class="form-group">
                        <label class="form-label">设置名称 <span class="required">*</span></label>
                        <el-input v-model.trim="form.name" maxlength="50" :placeholder="namePlaceholder"
                            :disabled="readonly" @focus="onDefaultFieldFocus('name')"
                            @blur="onDefaultFieldBlur('name')"></el-input>
                    </div>
                    <div class="form-group">
                        <label class="form-label">精彩瞬间说明</label>
                        <el-input type="textarea" :rows="3" v-model="form.description"
                            :placeholder="descPlaceholder" :disabled="readonly"
                            @focus="onDefaultFieldFocus('description')"
                            @blur="onDefaultFieldBlur('description')"></el-input>
                    </div>
                    <div class="form-group" style="margin-bottom:0">
                        <label class="form-label">适用用户组 <span class="required">*</span></label>
                        <div class="user-group-grid">
                            <label class="user-group-chip" :class="{ 'is-checked': isGroupChecked(group.id), 'is-disabled': readonly }"
                                v-for="group in groupChipOptions" :key="group.id">
                                <input type="checkbox" :value="group.id" :checked="isGroupChecked(group.id)"
                                    :disabled="readonly" @change="toggleGroup(group.id, $event.target.checked)" />
                                {{ group.name }}
                            </label>
                        </div>
                    </div>
                </div>

                <div class="card-block">
                    <div class="form-group" style="margin-bottom:0">
                        <label class="form-label">作答天数 <span class="required">*</span></label>
                        <div class="date-summary-bar">{{ dateSummary }}</div>
                        <date-multi-calendar v-model="form.dates" :disabled="readonly"></date-multi-calendar>
                    </div>
                </div>

                <div class="card-block">
                    <div class="form-group">
                        <label class="form-label">允许的文件类型 <span class="required">*</span></label>
                        <div class="user-group-grid">
                            <label class="user-group-chip" :class="{ 'is-checked': allowImage, 'is-disabled': readonly }">
                                <input type="checkbox" :checked="allowImage" :disabled="readonly"
                                    @change="toggleFileType('image', $event.target.checked)" />
                                图片（JPG/PNG/HEIC）
                            </label>
                            <label class="user-group-chip" :class="{ 'is-checked': allowVideo, 'is-disabled': readonly }">
                                <input type="checkbox" :checked="allowVideo" :disabled="readonly"
                                    @change="toggleFileType('video', $event.target.checked)" />
                                视频（MP4/MOV）
                            </label>
                        </div>
                    </div>
                    <div class="limit-grid">
                        <div class="form-group" style="margin-bottom:0">
                            <label class="form-label">图片最小数量</label>
                            <el-input v-model="form.minImageCount" :disabled="readonly || !allowImage"
                                @keydown.native="onDigitKeydown" @input="onPureNumber('minImageCount', 99)">
                                <template slot="append">张</template>
                            </el-input>
                        </div>
                        <div class="form-group" style="margin-bottom:0">
                            <label class="form-label">视频最小数量</label>
                            <el-input v-model="form.minVideoCount" :disabled="readonly || !allowVideo"
                                @keydown.native="onDigitKeydown" @input="onPureNumber('minVideoCount', 99)">
                                <template slot="append">个</template>
                            </el-input>
                        </div>
                        <div class="form-group" style="margin-bottom:0">
                            <label class="form-label">视频最大大小</label>
                            <el-input v-model="form.maxVideoMb" :disabled="readonly || !allowVideo"
                                @keydown.native="onDigitKeydown" @input="onPureNumber('maxVideoMb')">
                                <template slot="append">MB</template>
                            </el-input>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 离开编辑页确认弹窗 -->
        <el-dialog
            title="提示"
            :visible.sync="leaveDialogVisible"
            width="420px"
            custom-class="log-leave-dialog"
            :close-on-click-modal="false"
            append-to-body>
            <div class="leave-dialog-body">还未保存数据，是否保存？</div>
            <span slot="footer" class="dialog-footer">
                <el-button @click="confirmLeaveWithoutSave">否</el-button>
                <el-button type="primary" :loading="saving" @click="confirmLeaveWithSave">是</el-button>
            </span>
        </el-dialog>

        <!-- 启用/取消启用/删除确认弹窗 -->
        <el-dialog
            :visible.sync="actionDialogVisible"
            width="400px"
            custom-class="log-action-dialog"
            :show-close="true"
            :close-on-click-modal="false"
            append-to-body>
            <div class="action-dialog-body">
                <div class="action-dialog-question">{{ actionDialogData.question }}</div>
                <div class="action-dialog-name">「{{ actionDialogData.name }}」</div>
                <div class="action-dialog-hint">{{ actionDialogData.hint }}</div>
            </div>
            <span slot="footer" class="dialog-footer">
                <el-button @click="actionDialogVisible = false">{{ actionDialogData.cancelText }}</el-button>
                <el-button type="primary" :loading="actionDialogData.loading" @click="confirmActionDialog">{{ actionDialogData.confirmText }}</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    import DateMultiCalendar from './components/date-multi-calendar.vue';
    import {
        ALL_GROUP_ID,
        isApiSuccess,
        apiMsg,
        apiData,
        displayGroupNames,
        formatDateLabel,
        getContiguousSegments,
        isAllGroups,
        normalizeUserGroupIds,
    } from './log-moment-shared.js';

    const DEFAULT_NAME = '精彩瞬间';
    const DEFAULT_DESC = '请分享研修过程中的精彩瞬间，记录学习与交流的美好时刻。';

    const emptyForm = () => ({
        name: DEFAULT_NAME,
        description: DEFAULT_DESC,
        userGroupIds: [ALL_GROUP_ID],
        dates: [],
        minImageCount: 1,
        minVideoCount: 0,
        maxVideoMb: 0,
        status: 0,
    });

    export default {
        components: { DateMultiCalendar },
        data() {
            return {
                loading: false,
                saving: false,
                view: 'list',
                readonly: false,
                editingId: null,
                list: [],
                userGroupOptions: [],
                fileTypes: ['image'],
                form: emptyForm(),
                namePlaceholder: '请输入设置名称',
                descPlaceholder: '请输入精彩瞬间说明，将展示给学员参考',
                formSnapshot: '',
                leaveDialogVisible: false,
                actionDialogVisible: false,
                actionDialogData: {
                    question: '',
                    name: '',
                    hint: '',
                    cancelText: '',
                    confirmText: '',
                    loading: false,
                    callback: null,
                },
            };
        },
        computed: {
            ...mapState({
                currentProject: (state) => state.user.currentProject,
            }),
            projectId() {
                return this.currentProject && this.currentProject.id;
            },
            groupChipOptions() {
                return [{ id: ALL_GROUP_ID, name: '全部学员' }].concat(this.userGroupOptions);
            },
            allowImage() {
                return this.fileTypes.indexOf('image') >= 0;
            },
            allowVideo() {
                return this.fileTypes.indexOf('video') >= 0;
            },
            detailLabel() {
                if (!this.editingId) return '';
                return (this.readonly ? '查看：' : '编辑：') + (this.form.name || '精彩瞬间设置');
            },
            dateSummary() {
                let n = (this.form.dates || []).length;
                let text = this.formatDatesSummary(this.form.dates);
                if (!n) return '共0天。学员需完成同等天数的精彩瞬间';
                return `${text}，共${n}天。学员需完成同等天数的精彩瞬间`;
            },
        },
        watch: {
            projectId: {
                immediate: true,
                handler(id) {
                    if (id) this.loadAll();
                },
            },
            view(val) {
                if (val === 'list') {
                    this.$nextTick(() => this.syncListHeight());
                } else {
                    let root = this.$refs.listViewRoot;
                    if (root) root.style.height = '';
                }
            },
        },
        methods: {
            displayGroupNames,
            formatDateLabel,
            getItemDayCount(item) {
                if (item.dates && item.dates.length) return item.dates.length;
                return (item.days || []).length;
            },
            mediaLabel(item) {
                let parts = [];
                if (item.allowImage === 1) parts.push('图片');
                if (item.allowVideo === 1) parts.push('视频');
                return parts.length ? parts.join('/') : '未配置类型';
            },
            formatGroupText(item) {
                let names = displayGroupNames(item, this.userGroupOptions);
                if (!names || !names.length) return '未配置用户组';
                if (isAllGroups(item && item.userGroupIds, this.userGroupOptions, item)) return '适用全部学员';
                if (names.length === 1) return `适用${names[0]}`;
                if (names.length === 2) return `适用${names[0]}、${names[1]}`;
                return `适用${names[0]}等${names.length}个用户组`;
            },
            formatUpdateTime(val) {
                if (!val) return '-';
                let s = String(val);
                if (s.length >= 16) return s.substring(0, 16);
                return s;
            },
            syncListHeight() {
                let sidebar = document.querySelector('.sidebar');
                if (!sidebar) return;
                let root = this.$refs.listViewRoot;
                if (!root) return;
                root.style.height = sidebar.offsetHeight + 'px';
                root.style.overflowY = 'auto';
                root.style.boxSizing = 'border-box';
            },
            formatDatesSummary(dates) {
                return getContiguousSegments(dates).map((seg) => {
                    if (seg.start === seg.end) return formatDateLabel(seg.start);
                    return `${formatDateLabel(seg.start)}至${formatDateLabel(seg.end)}`;
                }).join('，');
            },
            normalizeForCompare() {
                return {
                    name: this.form.name || '',
                    description: this.form.description || '',
                    userGroupIds: (this.form.userGroupIds || []).map(Number).slice().sort((a, b) => a - b),
                    dates: (this.form.dates || []).slice().sort(),
                    minImageCount: this.form.minImageCount,
                    minVideoCount: this.form.minVideoCount,
                    maxVideoMb: this.form.maxVideoMb,
                    fileTypes: this.fileTypes.slice().sort(),
                };
            },
            takeSnapshot() {
                this.formSnapshot = JSON.stringify(this.normalizeForCompare());
            },
            isDirty() {
                return this.formSnapshot !== JSON.stringify(this.normalizeForCompare());
            },
            isGroupChecked(id) {
                if (Number(id) === ALL_GROUP_ID) {
                    return isAllGroups(this.form.userGroupIds, this.userGroupOptions);
                }
                return (this.form.userGroupIds || []).some((x) => Number(x) === Number(id));
            },
            toggleGroup(id, checked) {
                let numId = Number(id);
                if (numId === ALL_GROUP_ID) {
                    this.form.userGroupIds = checked ? [ALL_GROUP_ID] : [];
                    return;
                }
                let wasAll = isAllGroups(this.form.userGroupIds, this.userGroupOptions);
                if (wasAll) {
                    this.form.userGroupIds = [numId];
                    return;
                }
                let ids = (this.form.userGroupIds || []).map(Number).filter((x) => x !== ALL_GROUP_ID);
                if (checked) {
                    if (ids.indexOf(numId) < 0) ids.push(numId);
                } else {
                    ids = ids.filter((x) => x !== numId);
                }
                if (this.userGroupOptions.length && ids.length >= this.userGroupOptions.length &&
                    this.userGroupOptions.every((g) => ids.indexOf(Number(g.id)) >= 0)) {
                    this.form.userGroupIds = [ALL_GROUP_ID];
                } else {
                    this.form.userGroupIds = ids;
                }
            },
            toggleFileType(type, checked) {
                if (checked) {
                    if (this.fileTypes.indexOf(type) < 0) this.fileTypes.push(type);
                } else {
                    this.fileTypes = this.fileTypes.filter((t) => t !== type);
                }
            },
            onDigitKeydown(e) {
                let allow = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
                if (allow.indexOf(e.key) >= 0) return;
                if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].indexOf(e.key.toLowerCase()) >= 0) return;
                if (!/^\d$/.test(e.key)) e.preventDefault();
            },
            onPureNumber(field, max) {
                let raw = String(this.form[field] == null ? '' : this.form[field]).replace(/\D/g, '');
                if (raw === '') {
                    this.form[field] = '';
                    return;
                }
                let n = Number(raw);
                if (max != null && n > max) n = max;
                this.form[field] = n;
            },
            toSaveNumber(val) {
                if (val === '' || val == null) return 0;
                let n = Number(val);
                return isNaN(n) ? 0 : n;
            },
            onDefaultFieldFocus(field) {
                if (this.readonly) return;
                if (field === 'name') {
                    if (this.form.name === DEFAULT_NAME) {
                        this.form.name = '';
                        this.namePlaceholder = DEFAULT_NAME;
                    }
                } else if (field === 'description') {
                    if (this.form.description === DEFAULT_DESC) {
                        this.form.description = '';
                        this.descPlaceholder = DEFAULT_DESC;
                    }
                }
            },
            onDefaultFieldBlur(field) {
                if (this.readonly) return;
                if (field === 'name') {
                    if (!(this.form.name || '').trim()) {
                        this.form.name = DEFAULT_NAME;
                        this.namePlaceholder = '请输入设置名称';
                    }
                } else if (field === 'description') {
                    if (!(this.form.description || '').trim()) {
                        this.form.description = DEFAULT_DESC;
                        this.descPlaceholder = '请输入精彩瞬间说明，将展示给学员参考';
                    }
                }
            },
            async loadAll() {
                if (!this.projectId) return;
                this.loading = true;
                try {
                    await Promise.all([this.fetchProjectInfo(), this.fetchList()]);
                } finally {
                    this.loading = false;
                    if (this.view === 'list') {
                        this.$nextTick(() => this.syncListHeight());
                    }
                }
            },
            async fetchProjectInfo() {
                let res = await this.$Api.StageV2.getProjectInfo({ projectId: this.projectId });
                if (!isApiSuccess(res)) return;
                let data = apiData(res) || {};
                this.userGroupOptions = (data.availableUserGroups || []).map((g) => ({
                    id: Number(g.id),
                    name: g.name,
                }));
            },
            async fetchList() {
                let res = await this.$Api.Moment.getTemplateList({ projectId: this.projectId });
                if (!isApiSuccess(res)) {
                    this.$message.error(apiMsg(res) || '获取精彩瞬间设置失败');
                    this.list = [];
                    return;
                }
                this.list = apiData(res) || [];
            },
            resetForm() {
                this.form = emptyForm();
                this.fileTypes = ['image'];
                this.namePlaceholder = '请输入设置名称';
                this.descPlaceholder = '请输入精彩瞬间说明，将展示给学员参考';
            },
            createSetting() {
                this.editingId = null;
                this.readonly = false;
                this.resetForm();
                this.view = 'detail';
                this.$nextTick(() => this.takeSnapshot());
            },
            async openDetail(id, readonly) {
                this.loading = true;
                try {
                    let res = await this.$Api.Moment.getTemplateDetail({ id });
                    if (!isApiSuccess(res)) {
                        this.$message.error(apiMsg(res) || '获取详情失败');
                        return;
                    }
                    let data = apiData(res) || {};
                    this.editingId = id;
                    this.readonly = !!readonly;
                    this.fileTypes = [];
                    if (data.allowImage === 1) this.fileTypes.push('image');
                    if (data.allowVideo === 1) this.fileTypes.push('video');
                    this.form = {
                        name: data.name || '',
                        description: data.description || '',
                        userGroupIds: normalizeUserGroupIds((data.userGroupIds || []).map(Number), this.userGroupOptions, data),
                        dates: (data.days || []).map((d) => d.momentDate).filter(Boolean),
                        minImageCount: data.minImageCount == null ? 1 : Number(data.minImageCount),
                        minVideoCount: data.minVideoCount == null ? 0 : Number(data.minVideoCount),
                        maxVideoMb: data.maxVideoSize ? Math.round(data.maxVideoSize / 1048576) : 0,
                        status: data.status || 0,
                    };
                    this.view = 'detail';
                    this.$nextTick(() => this.takeSnapshot());
                } finally {
                    this.loading = false;
                }
            },
            onBackToList() {
                if (this.readonly || !this.isDirty()) {
                    this.backToList();
                    return;
                }
                this.leaveDialogVisible = true;
            },
            confirmLeaveWithoutSave() {
                this.leaveDialogVisible = false;
                this.backToList();
            },
            async confirmLeaveWithSave() {
                let ok = await this.saveDetail(true);
                if (ok) this.leaveDialogVisible = false;
            },
            backToList() {
                this.leaveDialogVisible = false;
                this.view = 'list';
                this.editingId = null;
                this.readonly = false;
                this.formSnapshot = '';
                this.fetchList();
            },
            buildPayload() {
                if (!this.form.name) {
                    this.$message.warning('请填写设置名称');
                    return null;
                }
                if (!this.form.userGroupIds.length) {
                    this.$message.warning('请至少选择一个适用用户组');
                    return null;
                }
                if (!this.form.dates.length) {
                    this.$message.warning('请在日历中选择作答日期');
                    return null;
                }
                if (!this.allowImage && !this.allowVideo) {
                    this.$message.warning('请至少选择一种文件类型');
                    return null;
                }
                let payload = {
                    name: this.form.name,
                    description: this.form.description,
                    projectId: this.projectId,
                    userGroupIds: this.form.userGroupIds.map(Number),
                    days: this.form.dates.slice().sort().map((dk) => ({ momentDate: dk })),
                    allowImage: this.allowImage ? 1 : 0,
                    allowVideo: this.allowVideo ? 1 : 0,
                    minImageCount: this.allowImage ? this.toSaveNumber(this.form.minImageCount) : 0,
                    minVideoCount: this.allowVideo ? this.toSaveNumber(this.form.minVideoCount) : 0,
                    maxVideoSize: this.allowVideo && this.toSaveNumber(this.form.maxVideoMb)
                        ? this.toSaveNumber(this.form.maxVideoMb) * 1048576 : 0,
                    minWordCount: 0,
                    needReview: 0,
                    status: this.editingId ? this.form.status : 0,
                };
                if (this.editingId) payload.id = this.editingId;
                return payload;
            },
            async saveDetail(goList) {
                let payload = this.buildPayload();
                if (!payload) return false;
                this.saving = true;
                try {
                    let res = this.editingId
                        ? await this.$Api.Moment.updateTemplate(payload)
                        : await this.$Api.Moment.createTemplate(payload);
                    if (isApiSuccess(res)) {
                        this.$message.success('保存成功');
                        this.takeSnapshot();
                        if (goList) this.backToList();
                        return true;
                    }
                    this.$message.error(apiMsg(res) || '保存失败');
                    return false;
                } catch (e) {
                    this.$message.error('保存失败');
                    return false;
                } finally {
                    this.saving = false;
                }
            },
            toggleStatus(item) {
                let next = item.status === 1 ? 0 : 1;
                if (next === 1) {
                    this.actionDialogData = {
                        question: '确定启用该精彩瞬间设置？',
                        name: item.name || '未命名',
                        hint: '启用后，将对适用用户组生效。',
                        cancelText: '暂不启用',
                        confirmText: '确认启用',
                        loading: false,
                        callback: () => this.doToggleStatus(item, next),
                    };
                } else {
                    this.actionDialogData = {
                        question: '确定取消启用该精彩瞬间设置？',
                        name: item.name || '未命名',
                        hint: '取消后，学员端将不再使用该设置。',
                        cancelText: '保持启用',
                        confirmText: '确认取消',
                        loading: false,
                        callback: () => this.doToggleStatus(item, next),
                    };
                }
                this.actionDialogVisible = true;
            },
            async doToggleStatus(item, next) {
                this.actionDialogData.loading = true;
                try {
                    let res = await this.$Api.Moment.setTemplateStatus({ id: item.id, status: next });
                    if (isApiSuccess(res)) {
                        item.status = next;
                        this.$message.success(next === 1 ? '已启用' : '已取消启用');
                        this.actionDialogVisible = false;
                    } else {
                        this.$message.error(apiMsg(res) || '操作失败');
                    }
                } catch (e) {
                    this.$message.error('操作失败');
                } finally {
                    this.actionDialogData.loading = false;
                }
            },
            removeSetting(item) {
                this.actionDialogData = {
                    question: '确定删除该精彩瞬间设置？',
                    name: item.name || '未命名',
                    hint: '删除后不可恢复。',
                    cancelText: '暂不删除',
                    confirmText: '确认删除',
                    loading: false,
                    callback: () => this.doRemoveSetting(item),
                };
                this.actionDialogVisible = true;
            },
            async doRemoveSetting(item) {
                this.actionDialogData.loading = true;
                try {
                    let res = await this.$Api.Moment.deleteTemplate({ id: item.id });
                    if (isApiSuccess(res)) {
                        this.$message.success('已删除');
                        this.actionDialogVisible = false;
                        await this.fetchList();
                    } else {
                        this.$message.error(apiMsg(res) || '删除失败');
                    }
                } catch (e) {
                    this.$message.error('删除失败');
                } finally {
                    this.actionDialogData.loading = false;
                }
            },
            confirmActionDialog() {
                if (this.actionDialogData.callback) {
                    this.actionDialogData.callback();
                }
            },
        },
        mounted() {
            if (this.view === 'list') {
                this.$nextTick(() => this.syncListHeight());
            }
            window.addEventListener('resize', this._onResize = () => {
                if (this.view === 'list') this.syncListHeight();
            });
        },
        beforeDestroy() {
            if (this._onResize) window.removeEventListener('resize', this._onResize);
        },
    };
</script>

<style lang="less" scoped>
    .moment-setting {
        padding: 20px 25px;
        box-sizing: border-box;
    }

    .list-view-root {
        display: flex;
        flex-direction: column;
    }

    .page-head {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        margin: 0 0 12px;
    }

    .setting-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
        flex: 1;
        overflow-y: auto;
        min-height: 0;
    }

    .setting-item {
        background: #fff;
        border: 1px solid #EBEEF5;
        border-radius: 8px;
        padding: 14px 18px;
        display: flex;
        align-items: flex-start;
        gap: 14px;
        transition: border-color 0.2s, box-shadow 0.2s;

        &:hover {
            border-color: #D0D3D8;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        }
    }

    .setting-item-left {
        flex: 1;
        min-width: 0;
    }

    .setting-item-title {
        font-size: 14px;
        font-weight: 600;
        color: #1F2A37;
        line-height: 22px;
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }

    .setting-item-desc {
        margin-top: 6px;
        font-size: 12px;
        font-weight: 400;
        color: #8B95A1;
        line-height: 20px;
    }

    .setting-item-right {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        flex-shrink: 0;
        gap: 6px;
    }

    .setting-item-actions {
        display: flex;
        gap: 8px;
        flex-shrink: 0;
        flex-wrap: wrap;
        justify-content: flex-end;
    }

    .setting-item-time {
        font-size: 12px;
        font-weight: 400;
        color: #B0B6BF;
        line-height: 20px;
        text-align: right;
    }

    .detail-back-bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        margin-bottom: 16px;
    }

    .detail-back-left {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
    }

    .detail-label {
        font-size: 13px;
        color: #909399;
    }

    .log-detail-shell {
        background: #fff;
        border-radius: 8px;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
        overflow: hidden;
    }

    .card-block {
        padding: 20px 24px;
        border-top: 1px solid #EBEEF5;

        &:first-child {
            border-top: none;
        }
    }

    .form-group {
        margin-bottom: 16px;
    }

    .form-label {
        display: block;
        margin-bottom: 8px;
        font-size: 14px;
        font-weight: 600;
        color: #303133;

        .required {
            color: #F56C6C;
            margin-left: 2px;
        }
    }

    .limit-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 16px;
        align-items: start;
    }

    .form-row-2 {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(180px, 1fr);
        gap: 16px;
        align-items: start;
    }

    .date-summary-bar {
        margin: 0 0 10px;
        padding: 8px 12px;
        background: #F5F7FA;
        border-radius: 6px;
        font-size: 13px;
        color: #606266;
        line-height: 1.5;
    }

    .user-group-grid {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 8px;
    }

    .user-group-chip {
        display: inline-flex;
        align-items: center;
        padding: 7px 12px;
        border: 1px solid #E6E8EB;
        border-radius: 8px;
        font-size: 13px;
        color: #5C6673;
        cursor: pointer;
        background: #FAFBFC;
        user-select: none;
        transition: all 0.15s ease;

        input {
            position: absolute;
            opacity: 0;
            width: 0;
            height: 0;
            pointer-events: none;
        }

        &:hover:not(.is-disabled) {
            background: #fff;
            border-color: #C0C4CC;
        }

        &.is-checked {
            background: #F3F8FF;
            border-color: #B7D4F8;
            color: #3A7BD5;
            font-weight: 400;
        }

        &.is-disabled {
            cursor: not-allowed;
            opacity: 0.7;
        }
    }

    .leave-dialog-body {
        font-size: 14px;
        color: #303133;
        line-height: 1.6;
        padding: 4px 0 8px;
    }

    .action-dialog-body {
        text-align: center;
        padding: 12px 0 4px;
    }

    .action-dialog-question {
        font-size: 16px;
        font-weight: 600;
        color: #1F2A37;
        margin-bottom: 12px;
    }

    .action-dialog-name {
        font-size: 15px;
        color: #3A7BD5;
        margin-bottom: 8px;
    }

    .action-dialog-hint {
        font-size: 13px;
        color: #8B95A1;
    }

    /deep/ .el-input__inner,
    /deep/ .el-textarea__inner {
        border-radius: 6px;
    }

    @media (max-width: 960px) {
        .limit-grid {
            grid-template-columns: 1fr;
        }
    }
</style>

<style lang="less">
    .log-leave-dialog {
        border-radius: 8px;
        overflow: hidden;

        .el-dialog__header {
            padding: 16px 20px 8px;
        }

        .el-dialog__body {
            padding: 8px 20px 12px;
        }

        .el-dialog__footer {
            padding: 8px 20px 16px;
        }
    }

    .log-action-dialog {
        border-radius: 12px;
        overflow: hidden;

        .el-dialog__header {
            padding: 12px 16px 0;

            .el-dialog__headerbtn {
                top: 12px;
                right: 12px;
            }
        }

        .el-dialog__body {
            padding: 8px 24px 16px;
        }

        .el-dialog__footer {
            padding: 8px 24px 20px;
            text-align: center;
        }
    }
</style>
